import {
  FieldValueExtractor,
  UpdateHandler,
  GlobalPlaceholderResolver,
  CreateSerializer,
  StoreSerializer,
  ContextProvider,
  ValidationHandler
} from './fieldRegistry';
import { MediaFile } from './types';

export const extractTextValue: FieldValueExtractor = (field, cardItem) => {
  const rawValue = cardItem?.[field.name] || '';
  return field.deserializer ? field.deserializer(rawValue) : rawValue;
};

export const extractMediaValue: FieldValueExtractor = (field, cardItem) => {
  return (cardItem?.[field.name] as unknown[])?.[0] || null;
};

export const createMediaUpdateHandler = (updateType: string): UpdateHandler => {
  return (field, currentValue, cardItem, cardId, context) => {
    const originalMedia = (cardItem?.[field.name] as unknown[])?.[0];
    const currentMedia = currentValue as MediaFile | null;

    const currentId = currentMedia?.id;
    const originalId = (originalMedia as MediaFile | undefined)?.id;

    if (currentId && currentId !== originalId) {
      context.updateFile(cardId, currentMedia, updateType);
    } else if (!currentId && originalId) {
      let fieldKey = null;

      if (updateType === 'audios') {
        fieldKey = 'audio_ids';
      } else if (updateType === 'pictures') {
        fieldKey = 'picture_ids';
      } else if (updateType === 'documents') {
        fieldKey = 'document_ids';
      } else if (updateType === 'CARD') {
        fieldKey = 'picture_ids';
      } else {
        fieldKey = 'video_ids';
      }

      context.addToBatch(fieldKey, []);
      context.addToBatch(updateType, []);
    }
  };
};

export const createTextUpdateHandler = (): UpdateHandler => {
  return (field, currentValue, cardItem, cardId, context) => {
    const sanitizedValue = field.sanitizer
      ? field.sanitizer(currentValue as string)
      : currentValue;

    let originalValue: unknown = cardItem?.[field.name] || '';
    if (field.deserializer) {
      originalValue = field.deserializer(originalValue);
    }

    // Both are compared without the spaces around them, so a field the author
    // left alone (or only re-spaced) keeps its stored value exactly.
    if ((currentValue as string).trim() !== String(originalValue).trim()) {
      context.addToBatch(field.name, sanitizedValue);
    }
  };
};

export const resolveMediaPlaceholder = (
  placeholderPath: string
): GlobalPlaceholderResolver => {
  return (field, globalPlaceholders) => {
    const value = placeholderPath
      .split('.')
      .reduce(
        (obj: Record<string, unknown> | undefined, key) =>
          obj?.[key] as Record<string, unknown> | undefined,
        globalPlaceholders
      );
    return value ? { id: value, mediaUrl: '' } : null;
  };
};

export const resolveTextPlaceholder: GlobalPlaceholderResolver = () => null;

export const createTextSerializer = (shouldTrim: boolean): CreateSerializer => {
  return (field, currentValue, data) => {
    // A new card leaves out a field it has no value for.
    if (currentValue === undefined) return;
    const sanitizedValue = field.sanitizer
      ? field.sanitizer(currentValue as string)
      : currentValue;
    const value = shouldTrim
      ? (sanitizedValue as string).trim()
      : (sanitizedValue as string);
    data[field.name] = value;
  };
};

export const createMediaSerializer = (
  targetArrayKey: string
): CreateSerializer => {
  return (field, currentValue, data) => {
    if (!data[targetArrayKey]) {
      data[targetArrayKey] = [];
    }

    if (Array.isArray(currentValue)) {
      currentValue.forEach((item: { id?: unknown }) => {
        if (item?.id) {
          (data[targetArrayKey] as unknown[]).push(item.id);
        }
      });
    } else if ((currentValue as { id?: unknown })?.id) {
      (data[targetArrayKey] as unknown[]).push(
        (currentValue as { id: unknown }).id
      );
    }
  };
};

export const createTextStoreSerializer = (): StoreSerializer => {
  return (field, currentValue, newItem) => {
    if (currentValue !== undefined && currentValue !== null) {
      newItem[field.name] = currentValue;
    }
  };
};

export const createMediaStoreSerializer = (): StoreSerializer => {
  return () => {
    // No-op: media fields are populated by backend response
  };
};

export const provideEmptyContext: ContextProvider = () => ({});

const isValuePresent = (value: unknown): boolean => {
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  return value !== null && value !== undefined;
};

export const validateMediaField: ValidationHandler = (field, value) => {
  if (field.validation?.type === 'required' && !isValuePresent(value)) {
    return {
      valid: false,
      error: `${field.label || field.name} is required`,
      fieldName: field.name
    };
  }
  return null;
};

export const validateTextField: ValidationHandler = () => {
  return null;
};
